import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View, } from 'react-native';
import Animated, { useAnimatedStyle, interpolate, Extrapolation, } from 'react-native-reanimated';
import { ReadiumView, } from 'react-native-readium';
import { useDebounce } from 'use-debounce';
import { useQueryClient } from '@tanstack/react-query';
import { getContentProgress, syncProgress, useProgressItemMutation } from '@annunciate/query';
import { analytics, onAVStarted, PlaybackEvent, PlaybackEventEmitter, PositionType, useAuthStateStore, usePlaybackStateStore, } from "@annunciate/core";
import { FAIcon, useReaderPreferences, useReaderBackgroundColor, TableOfContents, ReaderSettings, useReaderColors, BookmarkButton, useEbookPlaybackState, makeEbookLocatorCompatible } from '@annunciate/ui';
import { contentToEbookTrack } from '@annunciate/ui/dist/utils/player';
import { ReaderButton } from './ReaderButton';
export const Reader = ({ playing, onTogglePlayer, position, }) => {
    const ref = useRef(undefined);
    const color = useReaderColors();
    const preferences = useReaderPreferences();
    const [toc, setToc] = useState([]);
    const [file, setFile] = useState();
    const [initialLocation, setInitialLocation] = useState();
    const [location, setLocation] = useState();
    const [debounceLocation] = useDebounce(location, 1000, {
        leading: true,
        trailing: true,
    });
    const { mutate } = useProgressItemMutation();
    const client = useQueryClient();
    const bkgColor = useReaderBackgroundColor();
    const { backgroundInverted: iconColor } = useReaderColors();
    const hasAuthInitialized = useAuthStateStore((state) => state.hasAuthInitialized);
    useEbookPlaybackState();
    const onScrubListener = useCallback(async ({ content, to, }) => {
        if (content.id !== playing.id)
            return;
        if (to.type !== PositionType.EBOOK)
            return;
        const toLocation = to.location;
        makeEbookLocatorCompatible(toLocation);
        const toTotalProgression = toLocation.locations?.totalProgression;
        await analytics.emitEbookEvent("pausing", content);
        await analytics.emitPlaybackEvent("seekStarted", {
            seek_position: toTotalProgression
        });
        setLocation(toLocation);
        return;
    }, [playing.id, setLocation]);
    useEffect(() => {
        PlaybackEventEmitter.on(PlaybackEvent.SCRUB, onScrubListener);
        return () => {
            PlaybackEventEmitter.off(PlaybackEvent.SCRUB, onScrubListener);
        };
    }, []);
    useEffect(() => {
        if (!hasAuthInitialized || !client) {
            setInitialLocation(undefined);
            return;
        }
        const initializeReaderAnalytics = async (position) => {
            const { playerIndex, content } = usePlaybackStateStore.getState();
            if (!content?.id)
                return;
            await analytics.emitEbookEvent("started", content, position);
            onAVStarted(content.id, position.locations?.totalProgression || 0, false);
            if (playerIndex !== 1) {
                await analytics.emitEbookEvent("pausing", content, position);
            }
        };
        const initialPosition = usePlaybackStateStore.getState().initialPosition;
        if (initialPosition && initialPosition.type === PositionType.EBOOK) {
            const { location: resumeLocation } = initialPosition;
            makeEbookLocatorCompatible(resumeLocation);
            setLocation(resumeLocation);
            setInitialLocation(resumeLocation);
            usePlaybackStateStore.setState({ initialPosition: undefined });
            initializeReaderAnalytics(resumeLocation);
            return;
        }
        const syncAndGetProgress = async () => {
            await syncProgress(client);
            const progressItem = getContentProgress(client, playing.id);
            if (progressItem?.position?.type === PositionType.EBOOK) {
                const { location: resumeLocation } = progressItem.position;
                makeEbookLocatorCompatible(resumeLocation);
                setLocation(resumeLocation);
                setInitialLocation(resumeLocation);
                initializeReaderAnalytics(resumeLocation);
            }
            else {
                setInitialLocation(null);
                initializeReaderAnalytics({
                    href: "",
                    type: "",
                    locations: {
                        totalProgression: 0,
                        progression: 0,
                    }
                });
            }
        };
        syncAndGetProgress();
    }, [playing.id, hasAuthInitialized, !!client]);
    useEffect(() => {
        if (playing && playing.asset?.src) {
            setFile(contentToEbookTrack(playing));
        }
        else {
            setFile(undefined);
        }
    }, [playing.id, playing.asset?.src]);
    const rotation = useAnimatedStyle(() => {
        const deg = interpolate(position.value, [1, 0.75], [0, 180], Extrapolation.CLAMP);
        return {
            opacity: 1,
            transform: [{
                    rotate: `${deg}deg`,
                }],
        };
    });
    if (initialLocation === undefined ||
        !file ||
        !hasAuthInitialized) {
        return (React.createElement(View, null,
            React.createElement(ActivityIndicator, { color: color.backgroundInverted })));
    }
    return (React.createElement(View, { style: { backgroundColor: bkgColor, flex: 1 } },
        React.createElement(View, { style: styles.controls },
            React.createElement(View, { style: styles.leftButtonContainer },
                React.createElement(Pressable, { onPress: onTogglePlayer, style: styles.button },
                    React.createElement(Animated.View, { style: rotation },
                        React.createElement(FAIcon, { name: "chevron-up", color: iconColor, size: 30 })))),
            React.createElement(View, { style: styles.rightButtonContainer },
                React.createElement(TableOfContents, { items: toc, onPress: (link) => {
                        const position = {
                            type: PositionType.EBOOK,
                            location: {
                                href: link.href,
                                type: "application/xhtml+xml",
                                title: link.title || "",
                            },
                        };
                        PlaybackEventEmitter.emit(PlaybackEvent.SCRUB, {
                            content: playing,
                            to: position,
                        });
                    } }),
                React.createElement(BookmarkButton, { item: playing, size: 30, buttonStyle: styles.bookmarkButton }),
                React.createElement(View, { style: styles.settings },
                    React.createElement(ReaderSettings, null)))),
        React.createElement(View, { style: { flexDirection: 'row' } },
            React.createElement(ReaderButton, { name: "chevron-left", onPress: () => {
                    ref.current?.prevPage();
                } }),
            React.createElement(View, { style: { width: '80%' } },
                React.createElement(ReadiumView, { ref: ref, file: { ...file, initialLocation }, style: {
                        height: 'calc(100vh - 50px)',
                    }, location: debounceLocation, preferences: preferences, onLocationChange: (newLocation) => {
                        setLocation(newLocation);
                        const position = {
                            type: PositionType.EBOOK,
                            location: newLocation,
                        };
                        const currentPlayerIndex = usePlaybackStateStore.getState().playerIndex;
                        if (currentPlayerIndex !== 1) {
                            return;
                        }
                        analytics.emitEbookEvent("playing", playing, newLocation);
                        mutate({
                            contentId: playing.id,
                            position,
                            sync: true,
                        });
                        return;
                    }, onTableOfContents: (toc) => setToc(toc) })),
            React.createElement(ReaderButton, { name: "chevron-right", onPress: () => {
                    ref.current?.nextPage();
                } }))));
};
const styles = StyleSheet.create({
    controls: {
        flexDirection: 'row',
        padding: 10,
    },
    leftButtonContainer: {
        flexGrow: 1,
        alignItems: 'flex-start',
        flexDirection: 'row',
    },
    button: {
        flexGrow: 1,
        alignItems: 'flex-start',
    },
    rightButtonContainer: {
        marginLeft: 'auto',
        flexDirection: 'row',
        gap: 24,
    },
    settings: {},
    bookmarkButton: {
        paddingVertical: 0,
        paddingHorizontal: 0,
    }
});
//# sourceMappingURL=Reader.js.map